import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Minus, ArrowRight, Database, Code2,
  Palette, Smartphone, PenLine, Megaphone, LayoutGrid, Film, Search as SearchIcon,
  Share2, Copy, Check, Zap, AlertTriangle, BookOpen, ShieldAlert, FileText,
} from "lucide-react";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";
import { localizeDigits } from "@/lib/digits";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  graphic_design:    Palette,
  web_dev:           Code2,
  app_dev:           Smartphone,
  content_writing:   PenLine,
  digital_marketing: Megaphone,
  ui_ux_design:      LayoutGrid,
  video_editing:     Film,
  seo_consulting:    SearchIcon,
};

const SAMPLE_CURL = `curl https://prolancer.app/api/public/market/prices`;
const SAMPLE_JSON = `{
  "ok": true,
  "data": {
    "period": "2026-02",
    "services": [{
      "id": "web_dev",
      "label_ar": "تطوير المواقع",
      "avg_price_sar": 2850,
      "min_price_sar": 800,
      "max_price_sar": 12500,
      "samples": 234,
      "trend_pct_30d": 5.2
    }]
  }
}`;

export default function MarketObservatory({ lang: initialLang }) {
  // Read lang from localStorage reactively so navigation from the main app
  // (which may have just updated the language) uses the latest value.
  const [lang, setLang] = useState(() =>
    initialLang || localStorage.getItem("pl_lang") || "ar"
  );
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const isAr = lang === "ar";
  // Digit style tracks the UI language — Arabic ⇒ Arabic-Indic numerals.
  const digits = isAr ? "ar" : "en";

  // Listen for language changes from other tabs / other routes.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "pl_lang" && e.newValue) setLang(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggleLang = () => {
    const next = lang === "ar" ? "en" : "ar";
    setLang(next);
    localStorage.setItem("pl_lang", next);
  };

  useEffect(() => {
    document.documentElement.setAttribute("dir", isAr ? "rtl" : "ltr");
    window.scrollTo(0, 0);
    document.title = isAr
      ? "مؤشرات أسعار العمل الحر في السعودية — Prolancer Market Observer"
      : "Saudi Freelance Price Indicators — Prolancer Market Observer";

    // SEO: meta description + canonical + Dataset JSON-LD for Google Search.
    const setMeta = (name, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const metaDesc = isAr
      ? "مؤشرات سعرية مبنية على بيانات مجمّعة من منصة برولانسر خلال آخر 30 يومًا للعمل الحر في السعودية — مفيدة للدراسات والتحليل والتسعير."
      : "Price indicators aggregated from Prolancer platform data over the last 30 days for Saudi freelance work — useful for research, analysis and pricing.";
    setMeta("description", metaDesc);
    setMeta("og:title", isAr ? "مؤشرات أسعار العمل الحر في السعودية" : "Saudi Freelance Price Indicators", "property");
    setMeta("og:description", metaDesc, "property");
    setMeta("og:url", "https://prolancer.app/market", "property");
    setMeta("og:type", "website", "property");

    // Canonical.
    let link = document.head.querySelector('link[rel="canonical"][data-market="1"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("data-market", "1");
      document.head.appendChild(link);
    }
    link.setAttribute("href", "https://prolancer.app/market");

    return () => {}; // tags stay — single-page nav will overwrite them on next visit
  }, [isAr]);

  // Dataset JSON-LD — rebuilt whenever live stats arrive so Google indexes
  // the current observation period + averages.
  useEffect(() => {
    if (!stats) return;
    const prev = document.head.querySelector('script[data-market-jsonld="1"]');
    if (prev) prev.remove();
    const data = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      "name": isAr
        ? "مؤشرات أسعار العمل الحر في السعودية — Prolancer"
        : "Saudi Freelance Price Indicators — Prolancer",
      "description": isAr
        ? "مؤشرات سعرية مبنية على بيانات مجمّعة من منصة برولانسر خلال آخر 30 يومًا، للدراسات والتحليل والتسعير. ليست إحصاءً رسميًا أو شاملًا للسوق."
        : "Price indicators aggregated from Prolancer platform data over the last 30 days, for research, analysis and pricing. Not an official or exhaustive market statistic.",
      "url": "https://prolancer.app/market",
      "license": "https://creativecommons.org/licenses/by/4.0/",
      "keywords": [
        "freelance pricing", "Saudi Arabia", "market data",
        "أسعار العمل الحر", "السعودية", "مؤشرات أسعار",
      ],
      "creator": { "@type": "Organization", "name": "Prolancer", "url": "https://prolancer.app" },
      "temporalCoverage": `last-${stats.window_days || 30}-days`,
      "dateModified": stats.last_updated,
      "variableMeasured": (stats.services || []).map((s) => ({
        "@type": "PropertyValue",
        "name": isAr ? s.label_ar : s.label_en,
        "value": s.avg_price_sar,
        "unitCode": "SAR",
        "sampleSize": s.samples,
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.marketJsonld = "1";
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }, [stats, isAr]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/market/stats`)
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const formatN = (n) => localizeDigits(
    new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0)),
    digits
  );

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(SAMPLE_CURL);
      setCopied(true);
      toast.success(isAr ? "تم النسخ" : "Copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const share = async () => {
    const url = `${window.location.origin}/market`;
    const text = isAr
      ? "مؤشرات أسعار العمل الحر في السعودية — Prolancer"
      : "Saudi Freelance Price Indicators — Prolancer";
    if (navigator.share) {
      try { await navigator.share({ title: "Prolancer Market", text, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(isAr ? "تم نسخ الرابط" : "Link copied");
    }
  };

  // Format `last_updated` ISO string → "5 أكتوبر 2026" / "5 October 2026".
  const formatLastUpdated = (iso) => {
    if (!iso) return isAr ? "—" : "—";
    try {
      const d = new Date(iso);
      const months = isAr
        ? ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]
        : ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const day = isAr ? localizeDigits(String(d.getDate()), "ar") : String(d.getDate());
      const year = isAr ? localizeDigits(String(d.getFullYear()), "ar") : String(d.getFullYear());
      return `${day} ${months[d.getMonth()]} ${year}`;
    } catch {
      return iso;
    }
  };

  const period = stats?.period || "—";
  const total = stats?.total_queries_30d || 0;
  const topList = stats?.top_services_7d || [];
  const lastUpdatedText = formatLastUpdated(stats?.last_updated);

  return (
    <div className="min-h-screen bg-[#182138] text-white">
      {/* Top brand strip — padded to respect the iOS notch / status-bar area. */}
      <header
        className="sticky top-0 z-30 border-b border-[#2e3a5c]/70 bg-[#182138]/85 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <Link to="/" data-testid="market-back-home" className="inline-flex items-center gap-2 text-sm font-bold hover:text-[#18c98f] transition-colors">
            <ArrowRight className={`h-4 w-4 ${isAr ? "" : "rotate-180"}`} />
            <span>{isAr ? "برولانسر" : "Prolancer"}</span>
          </Link>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#18c98f]">
            <Database className="h-3.5 w-3.5" />
            {isAr ? "مرصد السوق" : "Market Observer"}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="market-lang-toggle"
              onClick={toggleLang}
              aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#18c98f]/40 text-[#18c98f] hover:bg-[#18c98f]/10 transition-colors text-[11px] font-black"
            >
              {isAr ? "EN" : "ع"}
            </button>
            <button
              data-testid="market-share"
              onClick={share}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full border border-[#18c98f]/50 text-[#18c98f] hover:bg-[#18c98f]/10 transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" />
              {isAr ? "شارك" : "Share"}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            background:
              "radial-gradient(1000px 500px at 20% -10%, rgba(24,201,143,0.4), transparent 60%)," +
              "radial-gradient(900px 500px at 95% 10%, rgba(24,201,143,0.3), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-6">
          {/* Top disclaimer banner — explicit statistical nature */}
          <div
            data-testid="market-top-disclaimer"
            className="mb-6 rounded-xl border border-[#f97316]/40 bg-[#f97316]/10 px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-[#f97316] mt-0.5" />
            <p className={`text-[12px] sm:text-[13px] leading-relaxed text-white/85 ${isAr ? "font-arabic" : "font-outfit"}`}>
              {isAr
                ? "البيانات المعروضة مؤشرات إحصائية من منصة برولانسر فقط، وليست صادرة عن جهة حكومية أو تنظيمية، ولا تمثل كامل سوق العمل الحر في المملكة."
                : "The data shown represents statistical indicators from the Prolancer platform only. They are not issued by any government or regulatory body and do not represent the entire Saudi freelance market."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-extrabold text-[#18c98f] border border-[#18c98f]/40 bg-[#18c98f]/10 rounded-full px-3 py-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#18c98f] animate-pulse" />
              {isAr ? "بيانات حيّة" : "Live data"}
            </div>
            {/* "Why it exists" chip — purpose for research/analysis */}
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white/80 border border-[#2e3a5c] bg-[#1f2a47] rounded-full px-3 py-1">
              <BookOpen className="h-3 w-3 text-[#18c98f]" />
              {isAr ? "للدراسات والتحليل والتسعير" : "For research, analysis & pricing"}
            </div>
          </div>

          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr
              ? "مؤشرات أسعار العمل الحر في السعودية"
              : "Saudi Freelance Price Indicators"}
          </h1>
          <p className={`mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-white/75 ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr
              ? "مؤشرات سعرية مبنية على بيانات مجمّعة من منصة برولانسر خلال آخر 30 يومًا، ولا تمثل إحصاءً رسميًا أو شاملًا للسوق."
              : "Price indicators aggregated from Prolancer platform data over the last 30 days — not an official or exhaustive market statistic."}
          </p>

          {/* Headline stats */}
          <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl">
            <HeadlineStat
              label={isAr ? "فترة الرصد" : "Period"}
              value={localizeDigits(period, digits)}
              dim={loading}
            />
            <HeadlineStat
              label={isAr ? "استعلام هذا الشهر" : "Queries · 30d"}
              value={formatN(total)}
              dim={loading}
            />
            <HeadlineStat
              label={isAr ? "الأعلى طلبًا · 7 أيام" : "Hottest · 7d"}
              value={topList[0] ? (isAr
                ? (stats?.services?.find((s) => s.id === topList[0])?.label_ar || topList[0])
                : (stats?.services?.find((s) => s.id === topList[0])?.label_en || topList[0]))
                : "—"}
              dim={loading}
              small
            />
          </div>

          {/* Last updated inline */}
          <div className={`mt-4 text-[11px] font-bold text-white/55 ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr ? "آخر تحديث: " : "Last updated: "}
            <span className="text-[#18c98f]">{lastUpdatedText}</span>
          </div>
        </div>
      </section>

      <SectionDivider theme="dark" />

      {/* SERVICE CARDS GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SectionHeading
          eyebrow={isAr ? "تفاصيل كل خدمة" : "Service breakdown"}
          title={isAr ? "متوسطات ونطاقات الأسعار" : "Price averages & ranges"}
          subtitle={isAr
            ? "كل بطاقة تعكس الحسابات الحقيقية التي أجراها مستقلون سعوديون خلال آخر ٣٠ يومًا."
            : "Each card reflects real calculations run by Saudi freelancers over the last 30 days."}
          lang={lang}
          theme="dark"
        />
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="market-services-grid">
          {(stats?.services || Array.from({ length: 8 })).map((s, i) => (
            <ServiceCard
              key={s?.id || i}
              service={s}
              lang={lang}
              digits={digits}
              onCalc={() => navigate(`/pricing?service=${s?.id || ""}`)}
            />
          ))}
        </div>
      </section>

      <SectionDivider theme="dark" />

      {/* API SECTION */}
      <section id="api" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SectionHeading
          eyebrow={isAr ? "بيانات مفتوحة" : "Open data"}
          title={isAr ? "API مجاني — استخدم البيانات في موقعك" : "Free public API — use the data on your site"}
          subtitle={isAr
            ? "endpoint مفتوح بدون مصادقة، ١٠٠ طلب/يوم مجانًا، ترخيص CC-BY-4.0. ضع رابطًا لـ prolancer.app/market عند الاستشهاد."
            : "Open, auth-free endpoint. 100 requests/day free, CC-BY-4.0. Link back to prolancer.app/market when citing."}
          lang={lang}
          theme="dark"
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="market-api-section">
          <div className="rounded-2xl border border-[#18c98f]/30 bg-[#1f2a47] p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold text-[#18c98f]">
                <Zap className="h-3.5 w-3.5" />
                {isAr ? "جرّب الآن" : "Try it now"}
              </div>
              <button
                data-testid="market-copy-curl"
                onClick={copyCurl}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border border-[#18c98f]/40 text-[#18c98f] hover:bg-[#18c98f]/10 transition"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {isAr ? (copied ? "نُسخ" : "انسخ") : (copied ? "Copied" : "Copy")}
              </button>
            </div>
            <pre dir="ltr" className="font-mono-num text-[12px] sm:text-[13px] bg-[#182138] border border-[#2e3a5c] rounded-lg p-3 overflow-x-auto whitespace-pre leading-6 text-[#18c98f]">
{SAMPLE_CURL}
            </pre>
            <p className="mt-3 text-[11px] text-white/60">
              {isAr
                ? "فلتر خدمة معيّنة: أضف ?service=web_dev"
                : "Filter by service: append ?service=web_dev"}
            </p>
          </div>

          <div className="rounded-2xl border border-[#2e3a5c] bg-[#1f2a47] p-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold text-white/70 mb-3">
              {isAr ? "عينة الاستجابة" : "Sample response"}
            </div>
            <pre dir="ltr" className="font-mono-num text-[11px] sm:text-[12px] bg-[#182138] border border-[#2e3a5c] rounded-lg p-3 overflow-x-auto whitespace-pre leading-5 text-white/85">
{SAMPLE_JSON}
            </pre>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ApiDetail title={isAr ? "الحد المجاني" : "Free quota"}  value={isAr ? "١٠٠ طلب / يوم" : "100 req / day"} digits={digits} />
          <ApiDetail title={isAr ? "الترخيص" : "License"}         value="CC-BY-4.0" />
          <ApiDetail title={isAr ? "التحديث" : "Updated"}         value={isAr ? "كل ٥ دقائق" : "Every 5 min"} />
        </div>

        <div className="mt-6 rounded-2xl p-5 bg-gradient-to-br from-[#18c98f]/10 to-transparent border border-[#18c98f]/30">
          <div className={`text-sm sm:text-base ${isAr ? "font-arabic" : "font-outfit"} text-white/85`}>
            {isAr
              ? "تريد حدًّا أعلى، بيانات يومية بالمدينة، و webhook تنبيهات؟ راسلنا على "
              : "Need higher limits, daily city-level data, and webhook alerts? Reach us at "}
            <a href="mailto:info@pro-lancer.net" className="text-[#18c98f] font-bold">info@pro-lancer.net</a>
            {isAr ? " للحصول على خطة Pro API." : " for a Pro API plan."}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section id="methodology" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SectionHeading
          eyebrow={isAr ? "منهجية البيانات" : "Data methodology"}
          title={isAr ? "كيف يتم تجميع هذه المؤشرات" : "How these indicators are built"}
          subtitle={isAr
            ? "شفافية كاملة حول مصدر الأرقام وكيفية استخدامها."
            : "Full transparency on where the numbers come from and how to use them."}
          lang={lang}
          theme="dark"
        />
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="market-methodology">
          <MethodItem
            icon={Database}
            title={isAr ? "المصدر" : "Source"}
            body={isAr
              ? "مجمّعة من Prolancer Market Observer من استعلامات التسعير على المنصة."
              : "Aggregated by Prolancer Market Observer from on-platform pricing queries."}
            lang={lang}
          />
          <MethodItem
            icon={BookOpen}
            title={isAr ? "النافذة الزمنية" : "Time window"}
            body={isAr
              ? "مبنية على آخر 30 يومًا، مع مقارنة بآخر 60 يومًا لحساب الاتجاه."
              : "Based on the last 30 days, compared with the previous 30 to compute the trend."}
            lang={lang}
          />
          <MethodItem
            icon={FileText}
            title={isAr ? "الهدف" : "Purpose"}
            body={isAr
              ? "تهدف للدراسات والتحليل وتطوير التسعير — ليست تسعيرة إلزامية."
              : "For research, analysis and pricing development — not a mandated rate."}
            lang={lang}
          />
          <MethodItem
            icon={ShieldAlert}
            title={isAr ? "لا تعتبر رسمية" : "Not official"}
            body={isAr
              ? "لا تمثل جهة حكومية أو تنظيمية، ولا تغطي كامل السوق."
              : "Not issued by any government or regulatory body; does not cover the full market."}
            lang={lang}
          />
        </div>
      </section>

      {/* Disclaimer */}
      <section id="disclaimer" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div
          data-testid="market-disclaimer"
          className="rounded-2xl p-5 sm:p-6 bg-[#1f2a47] border border-[#f97316]/40"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold text-[#f97316] mb-3">
            <ShieldAlert className="h-3.5 w-3.5" />
            {isAr ? "إخلاء مسؤولية" : "Disclaimer"}
          </div>
          <p className={`text-sm sm:text-base leading-relaxed text-white/80 ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr
              ? "هذه البيانات مؤشرات سعرية وإحصائية مبنية على بيانات مجمّعة من منصة برولانسر، ولا تمثل إحصاءً رسميًا أو شاملًا للسوق السعودي، ولا تُعد تسعيرة إلزامية أو توصية مهنية أو مالية."
              : "This data represents price and statistical indicators aggregated from the Prolancer platform. It does not represent an official or exhaustive statistic of the Saudi market, and does not constitute a binding rate or professional/financial recommendation."}
          </p>
        </div>
      </section>

      {/* Source + License + Last updated */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div
          data-testid="market-source-strip"
          className="rounded-2xl border border-[#2e3a5c] bg-[#0f1a30] px-4 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[12px]"
        >
          <SourceItem label={isAr ? "المصدر" : "Source"} value="Prolancer Market Observer" />
          <SourceItem
            label={isAr ? "الرابط" : "URL"}
            value={
              <a href="https://prolancer.app/market" className="text-[#18c98f] hover:underline font-mono-num" dir="ltr">
                prolancer.app/market
              </a>
            }
          />
          <SourceItem
            label={isAr ? "الترخيص" : "License"}
            value={
              <a href="https://creativecommons.org/licenses/by/4.0/" className="text-[#18c98f] hover:underline font-mono-num" dir="ltr">
                CC BY 4.0
              </a>
            }
          />
          <SourceItem label={isAr ? "آخر تحديث" : "Last updated"} value={lastUpdatedText} />
        </div>
      </section>

      {/* 3 key points about the observatory */}
      <section id="about-observatory" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <SectionHeading
          eyebrow={isAr ? "عن المرصد" : "About the observatory"}
          title={isAr ? "٣ نقاط رئيسية" : "Three key points"}
          subtitle={isAr
            ? "لمحة سريعة توضّح طبيعة المرصد ولمن يفيد."
            : "A quick overview of what the observatory is and who benefits from it."}
          lang={lang}
          theme="dark"
        />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="market-about-points">
          <AboutPoint
            n="1"
            title={isAr ? "مؤشرات شفافة لا تسعيرة" : "Transparent indicators, not a rate"}
            body={isAr
              ? "المرصد يعرض متوسطات مستخلصة من استعلامات حقيقية في برولانسر خلال آخر ٣٠ يومًا — ليست أسعارًا مُلزمة ولا توصية. الهدف إعطاء المستقل والعميل مرجعًا رقميًا يبدأ منه، لا أن يُغلق به التفاوض."
              : "The observatory shows averages extracted from real Prolancer queries in the last 30 days — not binding prices or a recommendation. The goal is to give both freelancers and clients a numeric reference to start from, not to end negotiations."}
            lang={lang}
          />
          <AboutPoint
            n="2"
            title={isAr ? "أداة بحث للدراسات والتحليل" : "A tool for research & analysis"}
            body={isAr
              ? "البيانات متاحة مجانًا تحت ترخيص CC BY 4.0 مع API مفتوح، لتُستخدم في الأبحاث الأكاديمية، تقارير القطاع، منشورات الصحافة، ولوحات تحكم الموارد البشرية — فقط اذكر المصدر عند الاستشهاد."
              : "The data is freely available under CC BY 4.0 with an open API, for use in academic research, industry reports, press pieces, and HR dashboards — just cite the source when you reference it."}
            lang={lang}
          />
          <AboutPoint
            n="3"
            title={isAr ? "تحديث مستمر مع وضوح العيّنات" : "Continuously updated with visible samples"}
            body={isAr
              ? "تُجدَّد الأرقام كل ٥ دقائق، ويُعرض عدد العيّنات لكل خدمة بوضوح. إذا كانت العيّنة محدودة (< ٥) تظهر شارة تحذير، وإذا لا توجد بيانات كافية يُحذف السعر بدل عرض صفر — لحماية القارئ من قرارات مبنية على أرقام ضعيفة."
              : "The numbers refresh every 5 minutes, with the sample count visibly displayed per service. If a sample is limited (< 5) a warning badge appears, and when there is not enough data the price is removed instead of showing zero — to protect readers from decisions built on weak numbers."}
            lang={lang}
          />
        </div>
      </section>

      {/* CTA Footer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-[#1f2a47] to-[#182138] border border-[#18c98f]/40 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold text-[#18c98f] mb-3">
            <TrendingUp className="h-3.5 w-3.5" />
            {isAr ? "احسب سعرك أنت" : "Calculate your own price"}
          </div>
          <h3 className={`text-2xl sm:text-3xl font-black ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr
              ? "رأيت المتوسطات — الآن احصل على اقتراحك الشخصي"
              : "You've seen the averages — now get your personalized quote"}
          </h3>
          <button
            data-testid="market-cta-calc"
            onClick={() => navigate("/pricing")}
            className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#18c98f] text-white font-black text-sm hover:bg-[#12a777] transition-colors"
          >
            {isAr ? "جرّب حاسبة التسعير" : "Open the pricing calculator"}
            <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />
          </button>
        </div>
      </section>
    </div>
  );
}

function HeadlineStat({ label, value, dim, small }) {
  return (
    <div className="rounded-xl border border-[#2e3a5c] bg-[#1f2a47] px-3 py-3 text-start" data-testid="market-headline-stat">
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-bold text-white/55 truncate">
        {label}
      </div>
      <div
        dir="ltr"
        className={`mt-1 font-black ${small ? "text-base sm:text-lg" : "text-2xl sm:text-3xl"} text-[#18c98f] font-mono-num leading-none ${dim ? "opacity-40" : ""}`}
      >
        {dim ? "— — —" : value}
      </div>
    </div>
  );
}

function MethodItem({ icon: Icon, title, body, lang }) {
  const isAr = lang === "ar";
  return (
    <div className="rounded-xl border border-[#2e3a5c] bg-[#1f2a47] p-4">
      <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#18c98f]/15 text-[#18c98f] mb-2">
        <Icon className="h-4 w-4" strokeWidth={2.5} />
      </div>
      <div className={`text-[13px] font-black mb-1 ${isAr ? "font-arabic" : "font-outfit"}`}>{title}</div>
      <div className={`text-[12px] leading-relaxed text-white/70 ${isAr ? "font-arabic" : "font-outfit"}`}>{body}</div>
    </div>
  );
}

function SourceItem({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/50">{label}</div>
      <div className="text-[13px] font-black text-white/90">{value}</div>
    </div>
  );
}

function AboutPoint({ n, title, body, lang }) {
  const isAr = lang === "ar";
  return (
    <div className="rounded-2xl border border-[#2e3a5c] bg-[#1f2a47] p-5 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-6 -end-4 text-[88px] font-black leading-none text-[#18c98f]/10 select-none pointer-events-none font-mono-num"
      >
        {n}
      </div>
      <div className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#18c98f]/15 text-[#18c98f] font-black text-sm mb-3 font-mono-num">
        {n}
      </div>
      <div className={`text-[15px] font-black mb-2 leading-tight ${isAr ? "font-arabic" : "font-outfit"}`}>
        {title}
      </div>
      <p className={`text-[13px] leading-relaxed text-white/75 ${isAr ? "font-arabic" : "font-outfit"}`}>
        {body}
      </p>
    </div>
  );
}

function ServiceCard({ service, lang, digits, onCalc }) {
  const isAr = lang === "ar";
  if (!service) {
    return <div className="h-48 rounded-2xl border border-[#2e3a5c] bg-[#1f2a47] animate-pulse" />;
  }
  const Icon = ICONS[service.id] || Code2;
  const trend = service.trend_pct_30d;
  const hasTrend = trend !== null && trend !== undefined;
  const TrendIcon = !hasTrend ? Minus : trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = !hasTrend
    ? "text-white/40"
    : trend > 0
      ? "text-[#18c98f]"
      : trend < 0
        ? "text-[#f97316]"
        : "text-white/50";
  const formatN = (n) => localizeDigits(new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0)), digits);
  const label = isAr ? service.label_ar : service.label_en;
  const samples = service.samples || 0;
  const hasData = samples > 0;
  const isLimited = hasData && samples < 5;
  return (
    <button
      type="button"
      onClick={onCalc}
      data-testid={`market-service-${service.id}`}
      className="group text-start rounded-2xl p-4 border border-[#2e3a5c] bg-[#1f2a47] hover:border-[#18c98f]/60 hover:shadow-[0_12px_30px_-20px_rgba(24,201,143,0.6)] transition-all"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#18c98f]/15 text-[#18c98f]">
            <Icon className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <div className={`text-[13px] font-black truncate max-w-[120px] ${isAr ? "font-arabic" : "font-outfit"}`}>
            {label}
          </div>
        </div>
        {hasData ? (
          <div className={`inline-flex items-center gap-1 text-[11px] font-black ${trendColor}`}>
            <TrendIcon className="h-3.5 w-3.5" />
            <span dir="ltr">{hasTrend ? `${trend > 0 ? "+" : ""}${localizeDigits(String(trend), digits)}%` : "—"}</span>
          </div>
        ) : null}
      </div>
      <div className="mt-3">
        <div className="text-[9px] uppercase tracking-widest font-bold text-white/50">
          {isAr ? "متوسط السعر المرصود" : "Observed avg price"}
        </div>
        {hasData ? (
          <div className="mt-0.5 flex items-baseline gap-1" dir="ltr">
            <div className="text-2xl sm:text-3xl font-black text-[#18c98f] font-mono-num leading-none">
              {formatN(service.avg_price_sar)}
            </div>
            <div className="text-[11px] font-bold text-white/60">{isAr ? "ر.س" : "SAR"}</div>
          </div>
        ) : (
          <div className={`mt-1 text-[12px] font-bold text-white/55 ${isAr ? "font-arabic" : "font-outfit"}`}>
            {isAr ? "لا توجد بيانات كافية" : "Not enough data yet"}
          </div>
        )}
      </div>
      {hasData && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-white/70">
          <div dir="ltr" className="rounded-md bg-[#182138] border border-[#2e3a5c] px-2 py-1">
            <span className="opacity-60">min </span>
            <span className="font-mono-num font-bold text-white">{formatN(service.min_price_sar)}</span>
          </div>
          <div dir="ltr" className="rounded-md bg-[#182138] border border-[#2e3a5c] px-2 py-1">
            <span className="opacity-60">max </span>
            <span className="font-mono-num font-bold text-white">{formatN(service.max_price_sar)}</span>
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-white/55">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span>
            {isAr ? "عدد العينات: " : "Samples: "}
            <span className="font-bold text-white/85 font-mono-num" dir="ltr">
              {localizeDigits(String(samples), digits)}
            </span>
          </span>
          {isLimited && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full bg-[#f97316]/15 border border-[#f97316]/40 text-[#f97316]">
              {isAr ? "عينة محدودة" : "limited"}
            </span>
          )}
        </div>
        <div className="inline-flex items-center gap-1 text-[#18c98f] font-bold opacity-0 group-hover:opacity-100 transition">
          {isAr ? "احسب" : "Calculate"}
          <ArrowRight className={`h-3 w-3 ${isAr ? "rotate-180" : ""}`} />
        </div>
      </div>
    </button>
  );
}

function ApiDetail({ title, value }) {
  return (
    <div className="rounded-xl border border-[#2e3a5c] bg-[#1f2a47] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/55">{title}</div>
      <div className="mt-1 text-sm font-black text-[#18c98f] font-mono-num">{value}</div>
    </div>
  );
}
